import React, { useCallback, useState } from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import ErrorMessage from '../../../Elements/ErrorMessage';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import fileHelper from '../../../Helpers/fileHelper';
import TemplateGroupItem from '../../../Domain/Template/TemplateGroupItem';
import TemplateVariantItem from '../../../Domain/Template/TemplateVariantItem';
import TemplateService from '../../../Services/TemplateService';
import TemplateContentEditor from './TemplateContentEditor';
import TemplateGroupSearchParams from './TemplateGroupSearchParams';
import TemplateWindowDescription from './TemplateWindowDescription';

// The template store, grouped by name; clicking a name opens the detail popup's "Variants" tab.
// Edit saves onto every language and version, while upload and download need one file and so are
// disabled for a multi-language template — NewestTemplateId is null in exactly that case.
const TemplateAdmin = () => {
  const [searchItem, setSearchItem] = useState<any>(() => new (TemplateGroupSearchParams as any)());
  // The item being worked on plus the ShownPanel of the open window; null closes every window
  const [selected, setSelected] = useState<any>(null);

  const refresh = useCallback((refreshFlag: boolean) => {
    setSelected(null);
    if (refreshFlag === true) {
      setSearchItem((previous: any) => ({ ...previous }));
    }
  }, []);

  const startSearch = useCallback((newSearchItem: any) => {
    setSearchItem({ ...newSearchItem });
  }, []);

  // The group row carries everything the windows need, the id being the newest version's — and that
  // row IS the newest version, which is what the content editor needs to know to allow editing.
  const openPanelForGroup = (item: any, shownPanel: string) =>
    setSelected({
      ...new (TemplateVariantItem as any)(),
      TemplateId: item.NewestTemplateId,
      Name: item.Name,
      Description: item.Description,
      Locale: item.Locale,
      Type: item.Type,
      Version: item.NewestVersion,
      IsLatestVersion: true,
      IsStringContent: item.IsStringContent,
      IsActive: item.IsActive,
      ShownPanel: shownPanel,
    });

  const downloadTemplateFile = (item: any) => {
    TemplateService.GetTemplateById(item.NewestTemplateId)
      .then((response: any) => {
        if (!response || !response.binaryContent) {
          ErrorMessage.SetErrorMessage('The template has no stored content to download.');
          return;
        }
        fileHelper.saveBase64File(response.binaryContent, `${response.name}.${response.type.toLowerCase()}`);
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: TA66. The template could not be downloaded. Please notify IT!');
      });
  };

  TemplateGroupItem.editFunction = (item: any) => openPanelForGroup(item, ShownPanelsEnum.EditItem);
  TemplateGroupItem.uploadFunction = (item: any) => openPanelForGroup(item, ShownPanelsEnum.FileUpload);
  TemplateGroupItem.editContentFunction = (item: any) => openPanelForGroup(item, ShownPanelsEnum.EditContent);
  TemplateGroupItem.downloadFunction = downloadTemplateFile;

  const renderOnePanel = (panelId: string, content: any) => (selected?.ShownPanel === panelId ? content : null);

  const renderPanels = () => {
    // One description covers every window, following the item's ShownPanel
    const templateWindow = (
      <GeneralEditWizzardBase
        crudType={CrudTypesEnum.Create}
        editedItem={selected}
        clickOnCancel={() => refresh(false)}
        clickOnSave={() => refresh(true)}
        windowDescription={TemplateWindowDescription}
        errorCode='TA80'
      />
    );

    // The same editor the Variants tab opens, on the newest version of a single-language template
    const contentWindow = (
      <TemplateContentEditor
        item={selected}
        clickOnCancel={() => refresh(false)}
        clickOnSave={() => refresh(true)}
      />
    );

    return (
      <div>
        {renderOnePanel(ShownPanelsEnum.AddNewItem, templateWindow)}
        {renderOnePanel(ShownPanelsEnum.EditItem, templateWindow)}
        {renderOnePanel(ShownPanelsEnum.FileUpload, templateWindow)}
        {renderOnePanel(ShownPanelsEnum.EditContent, contentWindow)}
      </div>
    );
  };

  const renderButtonsAndPanels = () => (
    <>
      <FigmaButtonRow
        buttons={
          [
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Right,
              onClick: () =>
                setSelected({ ...new (TemplateVariantItem as any)(), ShownPanel: ShownPanelsEnum.AddNewItem }),
              label: 'Create Template',
              tooltip: 'Create Template',
              iconName: 'fa-solid fa-plus-circle',
            },
          ] as any
        }
      />
      {renderPanels()}
    </>
  );

  return (
    <div className='contentwrapper'>
      {renderButtonsAndPanels()}
      <TabOfDataTable
        type={TemplateGroupItem}
        serviceFunction={TemplateService.GetTemplateGroupsBySearching}
        convertDBItemsFunction={TemplateService.ConvertDBGroupItems}
        searchItem={searchItem}
        searchParamsType={TemplateGroupSearchParams}
        clickOnStartSearch={startSearch}
        shownPanel={selected?.ShownPanel ?? ShownPanelsEnum.None}
        showSearchStrip={true}
        popupEnabled={true}
        orderBy='Name'
        orderDirection={OrderDirectionsEnum.Asc}
        singleSearchLine={true}
        singleSearchLinePlaceholder='Search in template name and description'
        errorCode='TA129'
      />
    </div>
  );
};

export default TemplateAdmin;
